import posthog from "posthog-js";

let initialized = false;

function requireConfig(value, variableName) {
  if (value) return true;

  if (process.env.NODE_ENV === "development")
    throw new Error(`${variableName} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${variableName} is configured`);

  return false;
}

/** Strip Firestore-style document ids from record URLs so pageviews stay useful without leaking entity ids. */
function sanitizePath(path) {
  if (!path || typeof path !== "string") return path;

  return path
    .replace(/\/record\/(company|product|customer|quote|invoice|project|warehouse|selling-point)\/[^/?#]+/gi, "/record/$1/:id")
    .replace(/\/blog\/[^/?#]+(\/|$)/gi, "/blog/:id$1")
    .replace(/\/administration\/(company|customer)\/[^/?#]+/gi, "/administration/$1/:id")
    .replace(/\/stripe\/checkout\/[^/?#]+\/[^/?#]+/gi, "/stripe/checkout/:company/:session");
}

function sanitizeUrl(url) {
  if (!url || typeof url !== "string") return url;

  try {
    const parsed = new URL(url, window.location.origin);
    parsed.pathname = sanitizePath(parsed.pathname);
    return parsed.toString();
  } catch {
    return sanitizePath(url);
  }
}

function sanitizeProperties(properties) {
  if (!properties) return properties;

  /* eslint-disable camelcase */
  if (properties.$current_url) properties.$current_url = sanitizeUrl(properties.$current_url);
  /* eslint-enable camelcase */
  if (properties.$pathname) properties.$pathname = sanitizePath(properties.$pathname);
  if (properties.$referrer) properties.$referrer = sanitizeUrl(properties.$referrer);

  return properties;
}

export function initPostHog() {
  const token = process.env.VUE_APP_POSTHOG_KEY;
  const host = process.env.VUE_APP_POSTHOG_HOST;

  if (!requireConfig(token, "VUE_APP_POSTHOG_KEY") || !requireConfig(host, "VUE_APP_POSTHOG_HOST")) return false;

  /* eslint-disable camelcase */
  posthog.init(token, {
    api_host: host,
    defaults: "2026-05-30",
    capture_pageview: "history_change",
    capture_exceptions: true,
    capture_performance: true,
    ui_host: "https://eu.posthog.com",
    sanitize_properties: (properties) => sanitizeProperties(properties),
  });
  /* eslint-enable camelcase */
  initialized = true;

  return true;
}

export function capture(eventName, properties) {
  if (initialized) posthog.capture(eventName, properties);
}

export function identify(distinctId, properties) {
  if (initialized && distinctId) posthog.identify(distinctId, properties);
}

export function captureException(error, properties) {
  if (initialized) posthog.captureException(error, properties);
}

export function reset() {
  if (initialized) posthog.reset();
}

export function getPostHogContext() {
  if (!initialized) return {};

  return {
    distinctId: posthog.get_distinct_id(),
    sessionId: posthog.get_session_id(),
  };
}

export function group(groupType, groupKey, properties) {
  if (initialized && groupKey) posthog.group(groupType, groupKey, properties);
}

export function register(properties) {
  if (initialized && properties) posthog.register(properties);
}
